import Icon, { IconType } from 'components/Icon';
import type { ShowcaseDesignSystem } from 'components/Modal/DsShowcaseModal';
import { useChatInputValidation } from 'components/Panel/Chat/hooks/useChatInputValidation';
import IconButton from 'components/Widget/Button/IconButton';
import ColorButton from 'components/Widget/Button/ColorButton';
import AttachmentStrip from 'components/Widget/AttachmentStrip';
import DsPicker from 'components/Widget/DsPicker';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import { Loader } from 'components/Widget/Loader';
import { Attachment, DesignSystem, Organization, SceneDsLink } from 'dragonmouse-shared/interfaces';
import { fetchSceneDsLink } from 'designSystem/util';
import { interceptIfNoCredits } from 'economy';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { MenuItemAction } from 'menu/MenuItem';
import { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import {
	selectCreditWallet,
	selectDesignSystems,
	selectIsFreeTier,
	selectIsGuest,
} from 'redux/db/reducers';
import {
	setPlanPricingModalVisible,
	showContextMenu,
	showDsShowcaseModal,
} from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import {
	FileUploadCallbacks,
	getInitials,
	maybeUploadPastedText,
	onFileDropped,
	onFilePasted,
	onUploadFileDialog,
	UploadTracker,
} from 'utils';
import {
	DESIGN_SYSTEM_PROMPT_EXAMPLES,
	DS_LINKED_PROMPT_EXAMPLES,
	PROMPT_EXAMPLES,
} from './constants';
import * as S from './styles';

interface CreatorInputConfig {
	submitButtonText: string;
	placeholder: string;
}

type CreatorInputMode = 'project' | 'designSystemPlan';

const MODE_CONFIG: Record<CreatorInputMode, CreatorInputConfig> = {
	project: {
		submitButtonText: 'Create Design',
		placeholder: 'Describe your design idea...',
	},
	designSystemPlan: {
		submitButtonText: 'Generate Plan',
		placeholder: 'Describe your design system, or upload images...',
	},
};

interface CreatorInputProps {
	onSubmit: (
		prompt: string,
		attachments: Attachment[],
		designSystemId?: string | null,
		designSystemVersionNumber?: number
	) => Promise<void>;
	disabled?: boolean;
	mode: CreatorInputMode;
	onDesignSystemSelect?: (designSystemId: string | null) => void;
	initialDesignSystemId?: string | null;
	onCreateBlank?: (
		designSystemId?: string | null,
		designSystemVersionNumber?: number
	) => Promise<void>;
	organizationId?: string | null;
	organizations?: Organization[];
	onOwnershipSelect?: (organizationId: string | null) => void;
}

const CreatorInput = ({
	onSubmit,
	disabled = false,
	mode,
	onDesignSystemSelect,
	initialDesignSystemId,
	onCreateBlank,
	organizationId,
	organizations,
	onOwnershipSelect,
}: CreatorInputProps) => {
	const config = MODE_CONFIG[mode];
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const theme = useTheme();
	const { isMobile, designSystems, creditWallet, isFreeTier, isGuest } =
		useSelector(
			(r: RootReducer) => ({
				isMobile: selectIsMobile(r.mode),
				designSystems: selectDesignSystems(r.db),
				creditWallet: selectCreditWallet(r.db),
				isFreeTier: selectIsFreeTier(r.db),
				isGuest: selectIsGuest(r.db),
			}),
			shallowEqual
		);

	const showCreditsCta =
		mode === 'project' &&
		(creditWallet
			? creditWallet.credits + creditWallet.promotionalCredits + creditWallet.purchaseCredits <= 0
			: true);

	const [prompt, setPrompt] = useState<string>('');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [loadingAttachments, setLoadingAttachments] = useState<UploadTracker[]>([]);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [undoStack, setUndoStack] = useState<string[]>([]);
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
	const [selectedDsLink, setSelectedDsLink] = useState<SceneDsLink | null>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const hasUserSelectedDs = useRef(false);
	const promptFromExample = useRef(false);
	const [isOwnershipOpen, setIsOwnershipOpen] = useState(false);
	const ownershipRef = useRef<HTMLDivElement>(null);
	useOnClickOutside(ownershipRef, () => setIsOwnershipOpen(false));

	// Close ownership dropdown on Escape
	useEffect(() => {
		const onEsc = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isOwnershipOpen) setIsOwnershipOpen(false);
		};
		window.addEventListener('keydown', onEsc);
		return () => window.removeEventListener('keydown', onEsc);
	}, [isOwnershipOpen]);

	const showOwnershipToolbar = organizations && organizations.length > 0 && onOwnershipSelect;

	const selectedOrgName = organizationId
		? organizations?.find((o) => o.id === organizationId)?.name ?? 'Team'
		: 'Personal';

	const isOrgSelected = !!organizationId;

	const {
		canSubmit: canSubmitBase,
		hasLoadingAttachments,
		minimalInputEntered,
	} = useChatInputValidation({
		prompt,
		loadingAttachments,
	});

	// flag for our slightly hacky override of the project mode
	// when this flag is true the button says "Create Blank Project" and submitting creates a blank project
	const blankProjectMode =
		mode === 'project' &&
		onCreateBlank &&
		!minimalInputEntered &&
		!hasLoadingAttachments &&
		attachments.length === 0;

	// Allow submission when either the traditional "canSubmitBase" is true or if we're in blankProjectMode
	const canSubmit = (canSubmitBase || blankProjectMode) && !disabled && !isSubmitting;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const handleSubmit = async () => {
		if (!canSubmit) return;

		// Set submitting state immediately to disable button and prevent multiple submissions
		setIsSubmitting(true);

		// Check credits early for non-blank projects (before duplicating a showcase DS)
		if (
			!blankProjectMode &&
			interceptIfNoCredits({ kind: 'creator', organizationId: organizationId ?? undefined })
		) {
			setIsSubmitting(false);
			return;
		}

		// If no input and onCreateBlank is provided, create blank project
		if (blankProjectMode) {
			try {
				await onCreateBlank(
					selectedDsLink?.designSystem.id,
					selectedDsLink?.linkedVersion.versionNumber
				);
			} catch (error) {
				// Error handling is done in parent component
			} finally {
				setIsSubmitting(false);
			}
			return;
		}

		try {
			await onSubmit(
				prompt,
				attachments,
				selectedDsLink?.designSystem.id,
				selectedDsLink?.linkedVersion.versionNumber
			);
			// Clear state after successful submission
			setPrompt('');
			setAttachments([]);
		} catch (error) {
			// Error handling is done in parent component
		} finally {
			setIsSubmitting(false);
		}
	};

	const onChangePrompt = (e: any) => {
		setPrompt(e.target.value);
		promptFromExample.current = false;
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		// Handle Ctrl+Z/Cmd+Z for undo
		if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
			if (undoStack.length > 0) {
				e.preventDefault();
				const previousPrompt = undoStack[undoStack.length - 1];
				setUndoStack(undoStack.slice(0, -1));
				setPrompt(previousPrompt);
				// Focus input after undo
				setTimeout(() => {
					inputRef.current?.focus();
				}, 0);
			}
			return;
		}

		// On desktop: Enter submits, Shift+Enter adds line break
		// On mobile: Enter adds line break, user taps button to submit
		if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
			e.preventDefault();
			if (canSubmit) {
				handleSubmit();
			}
		}
	};

	const onRemoveAttachment = (storagePath: string) => {
		setAttachments(attachments.filter((a) => a.storagePath !== storagePath));
	};

	const handlePaste = (text: string) => {
		// Insert the pasted text at the current cursor position in the prompt
		promptFromExample.current = false;
		const textarea = inputRef.current;
		if (textarea) {
			const start = textarea.selectionStart;
			const end = textarea.selectionEnd;
			const newPrompt = prompt.substring(0, start) + text + prompt.substring(end);
			setPrompt(newPrompt);

			// Focus the input after pasting
			setTimeout(() => {
				textarea.focus();
				textarea.setSelectionRange(start + text.length, start + text.length);
			}, 0);
		} else {
			// Fallback if textarea ref is not available
			setPrompt(prompt + text);
			setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
		}
	};

	const onContextMenu = async (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (e.target instanceof HTMLElement) {
			const paste = await MenuItemAction.createPasteText(handlePaste);
			const menuItems = [paste];
			const contextMenuAction = showContextMenu({
				point: { x: e.clientX + 5, y: e.clientY },
				menuItems,
			});
			dispatch(contextMenuAction);
		}
	};

	// File upload callbacks with functional setState to avoid stale closures
	const fileCallbacks: FileUploadCallbacks = useMemo(
		() => ({
			onLoadingStart: (uploadTracker: UploadTracker) => {
				setLoadingAttachments((prev) => [...prev, uploadTracker]);
			},
			onLoadingEnd: (uniqueId: string) => {
				setLoadingAttachments((prev) => prev.filter((a) => a.uniqueId !== uniqueId));
			},
			onSuccess: (attachment: Attachment) => {
				setAttachments((prev) => {
					// Check if file already exists
					if (prev.some((a) => a.storagePath === attachment.storagePath)) {
						toast.warning('This file is already attached');
						return prev;
					}
					return [...prev, attachment];
				});
			},
			onError: (error: Error) => {
				console.error('Error uploading file:', error);
			},
		}),
		[]
	);

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer.types.includes('Files')) {
			setIsDragging(true);
		}
	};

	const onDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		// Only reset if leaving the prompt container (not entering a child)
		if (!e.currentTarget.contains(e.relatedTarget as Node)) {
			setIsDragging(false);
		}
	};

	const onDrop = async (e: React.DragEvent) => {
		setIsDragging(false);
		if (loadingAttachments.length > 0) {
			toast.info('Upload in progress. Please wait.');
			return;
		}
		await onFileDropped(fileCallbacks)(e);
	};

	const pickRandomExample = (examples: string[]) => {
		let randomIndex;
		do {
			randomIndex = Math.floor(Math.random() * examples.length);
		} while (randomIndex === lastSelectedIndex && examples.length > 1);
		setLastSelectedIndex(randomIndex);
		return examples[randomIndex];
	};

	const handleTryExample = () => {
		// Save current prompt to undo stack if it exists
		if (prompt.trim().length > 0) {
			setUndoStack([...undoStack, prompt]);
		}

		// When a DS is selected in project mode, use content-only examples
		const examples =
			mode === 'project'
				? selectedDsLink
					? DS_LINKED_PROMPT_EXAMPLES
					: PROMPT_EXAMPLES
				: DESIGN_SYSTEM_PROMPT_EXAMPLES;

		setPrompt(pickRandomExample(examples));
		promptFromExample.current = true;

		// Focus input after setting example
		setTimeout(() => {
			inputRef.current?.focus();
		}, 0);
	};

	// Swaps the prompt to a random example from the appropriate list when DS selection changes
	const swapExamplePrompt = (dsSelected: boolean) => {
		if (!promptFromExample.current) return;
		setUndoStack((prev) => [...prev, prompt]);
		const examples = dsSelected ? DS_LINKED_PROMPT_EXAMPLES : PROMPT_EXAMPLES;
		setPrompt(pickRandomExample(examples));
	};

	const handleDesignSystemSelect = async (designSystem: DesignSystem | null) => {
		hasUserSelectedDs.current = true;

		if (!designSystem) {
			setSelectedDsLink(null);
			onDesignSystemSelect?.(null);
			swapExamplePrompt(false);
			return;
		}

		const dsLink = await fetchSceneDsLink(designSystem.id);
		if (!dsLink) {
			setSelectedDsLink(null);
			onDesignSystemSelect?.(null);
			return;
		}

		setSelectedDsLink(dsLink);
		onDesignSystemSelect?.(designSystem.id);
		swapExamplePrompt(true);
	};

	const handleCreateNewDesignSystem = () => {
		navigate(routes.createDesignSystem());
	};

	const onShowcaseSelect = async (item: ShowcaseDesignSystem) => {
		hasUserSelectedDs.current = true;
		setSelectedDsLink(null);

		const dsLink = await fetchSceneDsLink(item.designSystemId);
		if (!dsLink) {
			onDesignSystemSelect?.(null);
			return;
		}

		setSelectedDsLink(dsLink);
		onDesignSystemSelect?.(item.designSystemId);
		swapExamplePrompt(true);
	};

	const onBrowseShowcase = () => {
		dispatch(showDsShowcaseModal({ onSelect: onShowcaseSelect }));
	};

	// Pre-select a DS when an initial ID is provided (e.g. from user default setting).
	// Skip if user has explicitly selected (including "No Design System") to avoid overriding their choice.
	useEffect(() => {
		if (!hasUserSelectedDs.current && initialDesignSystemId && !selectedDsLink) {
			const ds = designSystems.find((d) => d.id === initialDesignSystemId);
			if (ds) {
				handleDesignSystemSelect(ds);
			}
		}
	}, [initialDesignSystemId, designSystems]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderAttachments = () => {
		if (!attachments.length && !loadingAttachments.length) return null;
		return (
			<AttachmentStrip
				attachments={attachments}
				loadingAttachments={loadingAttachments}
				onRemove={onRemoveAttachment}
			/>
		);
	};

	const renderAttachmentButton = () => {
		return (
			<IconButton
				icon={IconType.ADD}
				tooltip="Upload attachment"
				onClick={() => onUploadFileDialog(fileCallbacks, true)}
			/>
		);
	};

	const renderActions = () => {
		return (
			<S.Actions isMobile={isMobile}>
				{!isMobile && renderAttachmentButton()}
				{mode === 'project' && (
					<DsPicker
						selection={selectedDsLink}
						designSystems={designSystems}
						onSelect={handleDesignSystemSelect}
						onCreateNew={handleCreateNewDesignSystem}
						onBrowseShowcase={onBrowseShowcase}
					/>
				)}
				<S.Spacer />
				{isSubmitting ? (
					<S.LoadingWrapper isMobile={isMobile}>
						<Loader size={isMobile ? 32 : 22} />
					</S.LoadingWrapper>
				) : (
					<ColorButton
						size="small"
						label={blankProjectMode ? 'Create Blank Project' : config.submitButtonText}
						icon={IconType.LINE_ARROW_UP}
						onClick={handleSubmit}
						disabled={!canSubmit}
					/>
				)}
			</S.Actions>
		);
	};

	const renderContextToolbar = () => {
		if (!showOwnershipToolbar) return null;
		return (
			<S.ContextToolbar>
				<S.ContextDropdownWrapper ref={ownershipRef}>
					<S.ContextDropdownButton onClick={() => setIsOwnershipOpen(!isOwnershipOpen)}>
						{isOrgSelected ? (
							<S.OrgIcon>{getInitials(selectedOrgName)}</S.OrgIcon>
						) : (
							<S.PersonalIcon>
								<Icon type={IconType.USER} width={10} height={10} color={theme.gray[0]} />
							</S.PersonalIcon>
						)}
						<span>{selectedOrgName}</span>
						<S.DropdownArrow>▾</S.DropdownArrow>
					</S.ContextDropdownButton>
					<S.ContextMenu $isOpen={isOwnershipOpen}>
						<S.ContextMenuItem
							onClick={() => {
								onOwnershipSelect(null);
								setIsOwnershipOpen(false);
							}}
						>
							<S.PersonalIcon>
								<Icon type={IconType.USER} width={10} height={10} color={theme.gray[0]} />
							</S.PersonalIcon>
							<span>Personal</span>
						</S.ContextMenuItem>
						{organizations.map((org) => (
							<S.ContextMenuItem
								key={org.id}
								onClick={() => {
									onOwnershipSelect(org.id);
									setIsOwnershipOpen(false);
								}}
							>
								<S.OrgIcon>{getInitials(org.name)}</S.OrgIcon>
								<span>{org.name}</span>
							</S.ContextMenuItem>
						))}
					</S.ContextMenu>
				</S.ContextDropdownWrapper>
			</S.ContextToolbar>
		);
	};

	return (
		<S.Base isMobile={isMobile}>
			<S.Prompt
				isFocused={isFocused}
				isDragging={isDragging}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDrop={onDrop}
			>
				{renderContextToolbar()}
				{renderAttachments()}
				<S.MultilineInputWrapper
					isMobile={isMobile}
					onPaste={async (e: React.ClipboardEvent) => {
						const text = e.clipboardData?.getData('text/plain');
						if (
							text &&
							maybeUploadPastedText(
								text,
								attachments.map((a) => a.filename),
								fileCallbacks
							)
						) {
							e.preventDefault();
							return;
						}
						await onFilePasted(fileCallbacks)(e);
					}}
				>
					<MultilineInput
						ref={inputRef}
						value={prompt}
						onInput={onChangePrompt}
						onKeyDown={onKeyDown}
						placeholder={config.placeholder}
						minLines={2}
						renderBorder={false}
						variant="large"
						onFocusChange={(focused) => setIsFocused(focused)}
						onContextMenu={onContextMenu}
					/>
				</S.MultilineInputWrapper>
				{renderActions()}
				{isDragging && (
					<S.DragOverlay>
						<S.DragOverlayContent>
							<Icon type={IconType.ATTACHMENT} width={32} height={32} color={theme.gray[1000]} />
							<S.DragOverlayText>Drop files to attach</S.DragOverlayText>
						</S.DragOverlayContent>
					</S.DragOverlay>
				)}
			</S.Prompt>
			<S.FooterRow>
				<S.FooterLink onClick={handleTryExample}>Try an example</S.FooterLink>
				{showCreditsCta && (isFreeTier || isGuest) && (
					<>
						<S.FooterSeparator>·</S.FooterSeparator>
						<S.FooterLink onClick={() => dispatch(setPlanPricingModalVisible(true))}>
							View plans &amp; pricing
						</S.FooterLink>
					</>
				)}
			</S.FooterRow>
		</S.Base>
	);
};

export default CreatorInput;
